import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { BOX_IMAGES } from '@/data/images';
import { GraduationCap, Award, Dumbbell, Heart, Target, Users, TrendingUp, Shield } from 'lucide-react';

const COACH_IMAGES = {
  running: 'https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/3tymmeqj_WhatsApp%20Image%202026-04-02%20at%2015.10.34.jpeg',
  squat: 'https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/seuzapen_WhatsApp%20Image%202026-04-02%20at%2015.10.35.jpeg',
  portrait: 'https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/pa1h9rcs_WhatsApp%20Image%202026-04-02%20at%2015.10.33.jpeg',
  barwork: 'https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/xa15u8mu_WhatsApp%20Image%202026-04-02%20at%2015.10.34%20%281%29.jpeg',
};

export default function AboutPage() {
  const [introRef, introVisible] = useScrollReveal(0.1);
  const [parcoursRef, parcoursVisible] = useScrollReveal(0.1);
  const [visionRef, visionVisible] = useScrollReveal(0.1);
  const [humainRef, humainVisible] = useScrollReveal(0.1);
  const [projetRef, projetVisible] = useScrollReveal(0.1);
  const [valuesRef, valuesVisible] = useScrollReveal(0.1);
  const [ctaRef, ctaVisible] = useScrollReveal(0.1);

  return (
    <div className="min-h-screen">
      <PageMeta
        title="À Propos — Nicolas Perroy"
        description="Découvrez Nicolas Perroy, fondateur de CrossFit Saint-Chamond. +15 ans d'expérience, Master en Préparation Physique, coach certifié CrossFit. HYROX Training Club, Loire."
        path="/about"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'À Propos' }]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/about"
        eyebrow="À propos"
        title="NICOLAS PERROY"
        highlight="PERROY"
        subtitle="Coach sportif depuis plus de 15 ans. Licence STAPS, Master Préparation Physique. S'entraîner avec exigence. Progresser avec intelligence."
        image={COACH_IMAGES.portrait}
        alt="Nicolas Perroy - Fondateur CrossFit Saint-Chamond"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'À propos' }]}
      />

      {/* Intro */}
      <section ref={introRef} className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12 items-center max-w-6xl mx-auto">
            <div className={`lg:col-span-3 transition-all duration-700 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="font-heading text-3xl text-dark mb-6">NICOLAS PERROY</h2>
              <p className="text-lg text-foreground leading-relaxed mb-5 italic">
                S'entraîner avec exigence. Progresser avec intelligence.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Coach sportif depuis plus de <strong className="text-dark">15 ans</strong>, j'accompagne tous types de profils, du débutant au sportif confirmé, avec une approche structurée, progressive et adaptée à chacun.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Issu d'un parcours en <strong className="text-dark">natation de haut niveau</strong>, j'ai construit mon expertise à travers une formation complète (<strong className="text-dark">Licence STAPS</strong>, <strong className="text-dark">Master en Préparation Physique, Mentale & Réathlétisation</strong>, <strong className="text-dark">BEESAN</strong>) et de nombreuses expériences sur le terrain : clubs sportifs, préparation physique, fitness et coordination technique.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Depuis 2015, le <strong className="text-dark">CrossFit</strong> est devenu une évidence dans mon parcours. Une méthode complète et exigeante, en parfaite cohérence avec ma vision de l'entraînement : développer des capacités durables, en respectant le corps et en construisant des bases solides.
              </p>
              <p className="text-dark font-semibold leading-relaxed">
                Aujourd'hui, mon objectif est simple : vous aider à devenir plus fort, mieux bouger, et progresser sur le long terme.
              </p>
            </div>
            <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${introVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img decoding="async" src={COACH_IMAGES.squat} alt="Nicolas Perroy - Overhead squat CrossFit" className="w-full h-72 object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parcours */}
      <section ref={parcoursRef} className="section-padding bg-background">
        <div className="container-custom max-w-4xl">
          <div className={`transition-all duration-700 ${parcoursVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-primary font-semibold text-sm tracking-widest uppercase">Parcours</span>
            <h2 className="font-heading text-3xl md:text-4xl text-dark mt-3 mb-8">UN PARCOURS CONSTRUIT SUR L'EXPÉRIENCE ET LA TRANSMISSION</h2>

            <p className="text-muted-foreground leading-relaxed mb-4">
              C'est dans la <strong className="text-dark">natation</strong> que tout a commencé. Pendant plus de 15 ans en compétition, j'ai appris la rigueur, la discipline et le goût de l'effort. Très vite, l'envie de transmettre s'est imposée naturellement. J'ai alors évolué dans le milieu sportif en encadrant tous types de publics, des bébés nageurs aux adultes, en passant par des sportifs en quête de performance.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Mon parcours m'a conduit à me former à la <strong className="text-dark">faculté STAPS de Lyon</strong>, où j'ai obtenu :
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: GraduationCap, title: 'Licence Entraînement Sportif', sub: 'STAPS Lyon' },
                { icon: Award, title: 'Master Préparation Physique, Mentale & Réathlétisation', sub: 'STAPS Lyon' },
                { icon: Shield, title: "Brevet d'État Éducateur Sportif", sub: 'Activités de la Natation' },
                { icon: Dumbbell, title: 'Coach certifié CrossFit', sub: 'CrossFit Level' },
              ].map(({ icon: Icon, title, sub }, i) => (
                <div key={i} className="flex items-start gap-4 bg-white p-5 rounded-xl">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-dark text-sm">{title}</h3>
                    <p className="text-muted-foreground text-xs mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-muted-foreground leading-relaxed">
              Ces expériences m'ont permis d'intervenir dans des environnements variés : clubs sportifs, salles de fitness, préparation physique, coordination technique… et de développer une <strong className="text-dark">vision globale de l'entraînement</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Vision - CrossFit */}
      <section ref={visionRef} className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img decoding="async" src={COACH_IMAGES.barwork} alt="Nicolas Perroy - Entraînement à la barre" className="w-full h-full object-cover opacity-15" loading="lazy" />
          <div className="absolute inset-0 bg-dark/95" />
        </div>
        <div className="container-custom relative z-10 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <div className={`transition-all duration-700 ${visionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <span className="text-primary font-semibold text-sm tracking-widest uppercase">Vision</span>
              <h2 className="font-heading text-3xl md:text-4xl text-white mt-3 mb-8">UNE VISION MODERNE ET COMPLÈTE DE L'ENTRAÎNEMENT</h2>

              <p className="text-white/70 leading-relaxed mb-4">
                En 2015, je découvre le CrossFit.
              </p>
              <p className="text-white font-heading text-2xl mb-6">
                Une révélation.
              </p>
              <p className="text-white/70 leading-relaxed mb-8">
                Une méthode complète, qui développe l'ensemble des qualités physiques : force, endurance, mobilité, coordination. Mais surtout, une approche cohérente avec ma vision : un entraînement <strong className="text-white">structuré</strong>, <strong className="text-white">progressif</strong> et <strong className="text-white">adapté à chacun</strong>.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Construire des bases solides avant de chercher la performance',
                  'Développer la force sans négliger la mobilité',
                  'Prévenir les blessures autant que développer les capacités',
                  'Adapter l\'entraînement à chaque individu',
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-5">
                    <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-primary text-xs font-bold">{i + 1}</span>
                    </div>
                    <span className="text-white/80 text-sm leading-relaxed">{p}</span>
                  </div>
                ))}
              </div>

              <p className="text-white/50 text-sm mt-8 italic">
                Parce qu'un entraînement efficace est avant tout un entraînement que l'on peut maintenir dans le temps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Humain */}
      <section ref={humainRef} className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className={`transition-all duration-700 ${humainVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-primary font-semibold text-sm tracking-widest uppercase">Philosophie</span>
            <h2 className="font-heading text-3xl md:text-4xl text-dark mt-3 mb-8">UNE APPROCHE CENTRÉE SUR L'HUMAIN</h2>

            <p className="text-muted-foreground leading-relaxed mb-4">
              Au-delà des méthodes et des programmations, je suis convaincu d'une chose :
            </p>
            <p className="text-dark font-heading text-2xl mb-6">
              Chaque personne est différente.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              C'est pourquoi j'accorde une importance particulière à <strong className="text-dark">l'écoute</strong>, à <strong className="text-dark">l'adaptation</strong> et à la <strong className="text-dark">qualité de l'accompagnement</strong>. Que vous soyez débutant, en reprise ou sportif confirmé, vous devez comprendre ce que vous faites, pourquoi vous le faites, et comment progresser.
            </p>
            <p className="text-muted-foreground leading-relaxed italic">
              Parce que la vraie progression ne se mesure pas seulement en performance, mais dans la durée.
            </p>
          </div>
        </div>
      </section>

      {/* Projet */}
      <section ref={projetRef} className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12 items-center max-w-6xl mx-auto">
            <div className={`lg:col-span-2 transition-all duration-700 ${projetVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img decoding="async" src={COACH_IMAGES.running} alt="Nicolas Perroy en compétition" className="w-full h-40 object-cover object-top" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img decoding="async" src={COACH_IMAGES.barwork} alt="Nicolas Perroy muscle-up" className="w-full h-40 object-cover" loading="lazy" />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg col-span-2">
                  <img decoding="async" src={BOX_IMAGES.groupBarbell} alt="Communauté CrossFit Saint-Chamond" className="w-full h-40 object-cover" loading="lazy" />
                </div>
              </div>
            </div>
            <div className={`lg:col-span-3 transition-all duration-700 delay-200 ${projetVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <span className="text-primary font-semibold text-sm tracking-widest uppercase">Le projet</span>
              <h2 className="font-heading text-3xl md:text-4xl text-dark mt-3 mb-6">POURQUOI CE PROJET</h2>

              <p className="text-muted-foreground leading-relaxed mb-4">
                Après plus de 20 ans dans le sport, une chose est devenue évidente : <strong className="text-dark">je voulais aller plus loin</strong>.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Créer un lieu à mon image. Un cadre structuré, exigeant, mais accessible. Un espace où l'on vient pour <strong className="text-dark">s'entraîner sérieusement</strong>, <strong className="text-dark">progresser réellement</strong>, et partager une <strong className="text-dark">dynamique collective forte</strong>.
              </p>
              <p className="text-dark font-semibold text-lg mb-6">
                Aujourd'hui, ce projet prend vie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={valuesRef} className="section-padding bg-white">
        <div className="container-custom">
          <div className={`text-center mb-16 transition-all duration-700 ${valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-primary font-semibold text-sm tracking-widest uppercase">Ce qui nous définit</span>
            <h2 className="font-heading text-4xl md:text-5xl text-dark mt-3">NOS VALEURS</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Target, title: 'Exigence', desc: "Un entraînement structuré et rigoureux, pour des résultats concrets et durables." },
              { icon: Users, title: 'Communauté', desc: "Un collectif soudé où chacun se soutient pour progresser ensemble." },
              { icon: Award, title: 'Encadrement', desc: "Un coaching professionnel, certifié, avec une vraie expertise en préparation physique." },
              { icon: TrendingUp, title: 'Progression', desc: "Un suivi adapté à votre niveau pour une progression constante et mesurable." },
              { icon: Heart, title: 'Humain', desc: "L'écoute et l'adaptation au cœur de chaque accompagnement." },
              { icon: Shield, title: 'Accessibilité', desc: "Accessible à tous : débutants, confirmés, jeunes et moins jeunes." },
            ].map((value, idx) => {
              const Icon = value.icon;
              return (
                <div
                  key={idx}
                  className={`group bg-background p-7 rounded-xl transition-all duration-500 hover:shadow-lg hover:-translate-y-1 ${
                    valuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${200 + idx * 80}ms` }}
                >
                  <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    <Icon className="h-5 w-5 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-lg text-dark mb-2">{value.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{value.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="relative overflow-hidden">
        <div className="absolute inset-0 bg-primary" />
        <div className="container-custom relative z-10 py-16 md:py-20 text-center">
          <div className={`transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">ET MAINTENANT ?</h2>
            <p className="text-white/80 text-lg mb-2 max-w-2xl mx-auto">
              Que vous souhaitiez reprendre le sport, améliorer vos performances ou simplement vous sentir mieux dans votre corps, vous êtes au bon endroit.
            </p>
            <p className="text-white font-semibold text-base mb-8 max-w-xl mx-auto">
              Le plus important n'est pas d'où vous partez. Mais ce que vous êtes prêt à construire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="w-full sm:w-auto bg-white hover:bg-white/90 text-primary font-heading tracking-wide px-10 py-7 rounded-xl transition-all duration-200 hover:scale-[1.03] hover:shadow-xl">
                  NOUS CONTACTER
                </Button>
              </Link>
              <Link to="/programmes">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-primary font-heading tracking-wide px-10 py-7 rounded-xl bg-transparent transition-all duration-200">
                  NOS PROGRAMMES
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
